import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface AsideConfig {
  title?: string;
  description?: string;
  width?: string;
  actions?: ReactNode;
}

interface AsideContextType {
  isOpen: boolean;
  content: ReactNode;
  config: AsideConfig;
  openAside: (content: ReactNode, config?: AsideConfig) => void;
  closeAside: () => void;
  updateConfig: (config: Partial<AsideConfig>) => void;
}

const AsideContext = createContext<AsideContextType | undefined>(undefined);

interface AsideProviderProps {
  children: ReactNode;
}

export const AsideProvider: React.FC<AsideProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<ReactNode>(null);
  const [config, setConfig] = useState<AsideConfig>({
    width: '500px',
  });

  const openAside = (newContent: ReactNode, newConfig?: AsideConfig) => {
    setContent(newContent);
    setConfig((prev) => ({ width: '500px', ...(newConfig ?? prev) }));
    setIsOpen(true);
  };

  const closeAside = () => {
    setIsOpen(false);
  };

  const updateConfig = (newConfig: Partial<AsideConfig>) => {
    setConfig((prev) => ({ ...prev, ...newConfig }));
  };

  return (
    <AsideContext.Provider
      value={{
        isOpen,
        content,
        config,
        openAside,
        closeAside,
        updateConfig,
      }}
    >
      {children}
    </AsideContext.Provider>
  );
};

export const useAsideContext = () => {
  const context = useContext(AsideContext);
  if (!context) {
    throw new Error('useAsideContext must be used within AsideProvider');
  }
  return context;
};
